import { useEffect, useState } from 'react';
import { Download, Share, SquarePlus, X } from 'lucide-react';
import { readJson, writeJson } from '../lib/storage';
import { Button, cx, Sheet } from './kit';

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let deferred: BeforeInstallPromptEvent | null = null;
const listeners = new Set<() => void>();
if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferred = e as BeforeInstallPromptEvent;
    listeners.forEach((fn) => fn());
  });
  window.addEventListener('appinstalled', () => {
    deferred = null;
    writeJson(null, 'installDismissed', Date.now());
    listeners.forEach((fn) => fn());
  });
}

function isStandalone(): boolean {
  return window.matchMedia?.('(display-mode: standalone)').matches || (navigator as Navigator & { standalone?: boolean }).standalone === true;
}

function isIos(): boolean {
  return /iphone|ipad|ipod/i.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

/**
 * "Ana ekrana ekle" kartı. Android/Chrome: yerel kurulum istemi. iOS Safari: adım adım talimat (iOS'ta programatik
 * istem yok). Kurulu uygulamada ve kapatıldıktan sonra 14 gün görünmez. iOS'ta kurulmamış PWA verisi 7 günde
 * silinebildiği için kurulum oturumu da korur.
 */
export function InstallHint({ className }: { className?: string }) {
  const [, force] = useState(0);
  const [iosSheet, setIosSheet] = useState(false);
  useEffect(() => {
    const fn = () => force((x) => x + 1);
    listeners.add(fn);
    return () => void listeners.delete(fn);
  }, []);

  const dismissedAt = readJson<number>(null, 'installDismissed');
  if (isStandalone()) return null;
  if (dismissedAt && Date.now() - dismissedAt < 14 * 86_400_000) return null;
  const ios = isIos();
  if (!deferred && !ios) return null;

  const dismiss = () => {
    writeJson(null, 'installDismissed', Date.now());
    force((x) => x + 1);
  };

  return (
    <>
      <div className={cx('relative flex items-center gap-4 rounded-3xl bg-surface p-4 shadow-card ring-1 ring-line/60', className)}>
        <img src="/icons/icon-192.png" alt="" className="size-12 rounded-2xl" />
        <div className="min-w-0 flex-1">
          <p className="text-[15px] font-bold">Uygulamayı ana ekrana ekle</p>
          <p className="text-[13px] text-muted">Tek dokunuşla aç, kartın hep yanında.</p>
        </div>
        <Button
          size="sm"
          icon={<Download className="size-4" />}
          onClick={async () => {
            if (deferred) {
              await deferred.prompt();
              const choice = await deferred.userChoice.catch(() => null);
              if (choice?.outcome === 'accepted') dismiss();
              deferred = null;
              force((x) => x + 1);
            } else setIosSheet(true);
          }}
        >
          Ekle
        </Button>
        <button type="button" onClick={dismiss} aria-label="Kapat" className="absolute right-2 top-2 grid size-7 place-items-center rounded-full text-muted">
          <X className="size-4" />
        </button>
      </div>
      <Sheet open={iosSheet} onClose={() => setIosSheet(false)} title="Ana ekrana ekle">
        <ol className="space-y-4 pb-4 text-[15px]">
          <li className="flex items-center gap-3">
            <span className="grid size-10 place-items-center rounded-xl bg-surface-2">
              <Share className="size-5 text-brand" />
            </span>
            Safari'nin alt çubuğundaki <b>Paylaş</b> düğmesine dokun.
          </li>
          <li className="flex items-center gap-3">
            <span className="grid size-10 place-items-center rounded-xl bg-surface-2">
              <SquarePlus className="size-5 text-brand" />
            </span>
            Listeden <b>Ana Ekrana Ekle</b>'yi seç.
          </li>
          <li className="flex items-center gap-3">
            <img src="/icons/icon-192.png" alt="" className="size-10 rounded-xl" />
            Sağ üstten <b>Ekle</b>'ye dokun — uygulama ana ekranında.
          </li>
        </ol>
      </Sheet>
    </>
  );
}
